import React, { useEffect, useState } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles, AlertTriangle, DollarSign, GitBranch, Bot, FileDown, Check, GaugeCircle } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

const SEEN_KEY = "cap_demo_tour_seen";

/**
 * Eight-step in-dashboard walkthrough for first-time demo visitors.
 *
 * Renders once — after the tour completes (or the user dismisses it), a flag
 * is written to localStorage so it doesn't re-open on future visits. Every
 * step fires `guided_tour_step` and completion fires `guided_tour_completed`
 * so the funnel is measurable from the admin analytics summary.
 *
 * Props
 *  - isDemo:      only render when the current user is a demo visitor
 *  - autoStart:   open automatically on first mount (e.g. after ?welcome=demo)
 *  - onNavigate:  optional callback the tour can use to switch dashboard tabs
 *                 (e.g. jump to the copilot tab on step 6)
 */
export default function GuidedTour({ isDemo, autoStart = true, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!isDemo) return;
    let seen = false;
    try { seen = !!localStorage.getItem(SEEN_KEY); } catch { /* noop */ }
    if (!seen && autoStart) setOpen(true);
  }, [isDemo, autoStart]);

  // Fire per-step analytics.
  useEffect(() => {
    if (open) trackEvent("guided_tour_step", { step: idx + 1, id: STEPS[idx].id });
  }, [idx, open]);

  const close = (completed) => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* noop */ }
    setOpen(false);
    if (completed) trackEvent("guided_tour_completed");
    // Reset for possible re-open via the "?" button.
    setTimeout(() => setIdx(0), 500);
  };

  const restart = () => { setIdx(0); setOpen(true); };

  // Floating question-mark button always available for demo users.
  if (!isDemo) return null;
  return (
    <>
      <button
        onClick={restart}
        title="Replay the guided tour"
        aria-label="Replay the guided tour"
        className="fixed bottom-6 left-6 z-[80] w-11 h-11 rounded-full text-white shadow-lg grid place-items-center hover:scale-105 transition-transform"
        style={{ background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 100%)" }}
        data-testid="demo-tour-restart"
      >
        <Sparkles size={16}/>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[95] grid place-items-end sm:place-items-center px-4 pb-6 sm:pb-0"
          style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(4px)" }}
          data-testid="demo-tour-modal"
        >
          <TourCard
            step={STEPS[idx]}
            idx={idx}
            total={STEPS.length}
            onPrev={idx > 0 ? () => setIdx(idx - 1) : null}
            onNext={idx < STEPS.length - 1 ? () => {
              const nx = idx + 1;
              const nextStep = STEPS[nx];
              if (nextStep.tab && onNavigate) onNavigate(nextStep.tab);
              setIdx(nx);
            } : null}
            onFinish={() => close(true)}
            onClose={() => close(false)}
          />
        </div>
      )}
    </>
  );
}

const STEPS = [
  {
    id: "portfolio-health",
    icon: GaugeCircle,
    tab: "portfolio",
    title: "Step 1 · Portfolio Health at a Glance",
    body: "This is your executive rollup — six live programmes, $37M allocated, split across On Track / At Risk / Off Track. Every figure here is fictional demonstration data. In production this replaces your Monday-morning status meeting.",
  },
  {
    id: "red-project",
    icon: AlertTriangle,
    tab: "portfolio",
    title: "Step 2 · A Deteriorating Milestone",
    body: "Supply Chain Optimization is Off Track — its Planning module go-live is under pressure. Diagnostics has already surfaced the underlying RAG signals; NAVBOT would DM you the moment it flipped.",
  },
  {
    id: "raid",
    icon: AlertTriangle,
    tab: "portfolio",
    title: "Step 3 · Drill into the Underlying RAID",
    body: "Two Critical items sit under Supply Chain: a peak-season cutover risk and a master-data cleanse delay. Each carries an owner. This is where the boardroom conversation would normally take three days of chasing.",
  },
  {
    id: "budget-schedule",
    icon: DollarSign,
    tab: "portfolio",
    title: "Step 4 · Schedule & Budget Exposure",
    body: "Actual $2.4M over on Warehouse Systems, forecast $200K over on Planning Software. Exposure is quantified per project and rolled up to the portfolio. No spreadsheets. No email reconciliation.",
  },
  {
    id: "dependency",
    icon: GitBranch,
    tab: "portfolio",
    title: "Step 5 · Cross-Project Dependency",
    body: "Data & Analytics is At Risk partly because it depends on ERP master data. When the ERP programme slipped, Analytics inherited the risk automatically — you see it as one story, not two.",
  },
  {
    id: "navbot",
    icon: Bot,
    tab: "copilot",
    title: "Step 6 · Ask NAVBOT for an Executive Read",
    body: "Try any of these: “Which projects are most likely to miss their next major milestone?” · “Summarise the top portfolio risks for the CIO.” · “Where is budget exposure increasing?” · “Prepare a board-level portfolio update.” NAVBOT reads only your authorised project data — no fabricated numbers.",
  },
  {
    id: "boardpack",
    icon: FileDown,
    tab: "portfolio",
    title: "Step 7 · Generate a Board Pack",
    body: "Click Board Pack in the header to produce a watermarked 20-slide PPTX in about ten seconds — sponsor summary, portfolio dashboard, per-project pages, RAID, financials, decisions required. In production the watermark is off and your logo appears on every slide.",
  },
  {
    id: "decision",
    icon: Check,
    tab: "portfolio",
    title: "Step 8 · The Recommended Decision",
    body: "Approve a de-scope of the Supply Chain planning module for the peak-season release, protect the cutover date, and re-plan for a Q2 re-attempt. That's the whole point of Capittance — every data point exists to shorten the distance to the next executive decision.",
  },
];

function TourCard({ step, idx, total, onPrev, onNext, onFinish, onClose }) {
  const Icon = step.icon;
  return (
    <div
      className="bg-white rounded-md shadow-2xl w-full max-w-md p-5 md:p-6 relative"
      style={{ borderTop: "4px solid #7A1F2B" }}
      onClick={(e) => e.stopPropagation()}
      data-testid="demo-tour-card"
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-800"
        aria-label="Close guided tour"
        data-testid="demo-tour-close"
      >
        <X size={18}/>
      </button>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-sm grid place-items-center text-white" style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
          <Icon size={16}/>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#7A1F2B]">Guided tour · {idx + 1} of {total}</div>
          <div className="font-display text-base md:text-lg font-extrabold tracking-tight text-zinc-900 leading-tight">
            {step.title}
          </div>
        </div>
      </div>
      <p className="text-sm text-zinc-700 leading-relaxed" data-testid="demo-tour-body">{step.body}</p>
      {/* Progress dots */}
      <div className="flex items-center gap-1.5 mt-4">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#7A1F2B]" : "w-1.5 bg-zinc-300"}`}
          />
        ))}
      </div>
      <div className="mt-5 flex items-center justify-between gap-2">
        <button
          onClick={onPrev}
          disabled={!onPrev}
          className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 disabled:opacity-30 inline-flex items-center gap-1"
          data-testid="demo-tour-prev"
        >
          <ChevronLeft size={13}/> Back
        </button>
        {onNext ? (
          <button
            onClick={onNext}
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
            data-testid="demo-tour-next"
          >
            Next <ChevronRight size={13}/>
          </button>
        ) : (
          <button
            onClick={onFinish}
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
            data-testid="demo-tour-finish"
          >
            Finish tour <Check size={13}/>
          </button>
        )}
      </div>
    </div>
  );
}
